import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield,
  Home,
  Users,
  CreditCard,
  ClipboardList,
  BarChart3,
  Mail,
  MessageCircle,
  LogOut,
} from 'lucide-react';

const API = "https://canada-billing.ameontech.com";

const NAV_ITEMS = [
  { key: 'dashboard', label: 'Dashboard', icon: Home },
  { key: 'users', label: 'Users', icon: Users },
  { key: 'subscriptions', label: 'Subscriptions', icon: CreditCard },
  { key: 'invoice-monitor', label: 'Invoice Monitor', icon: ClipboardList },
  { key: 'revenue', label: 'Revenue', icon: BarChart3 },
  { key: 'contact-forms', label: 'Contact Forms', icon: Mail },
  { key: 'feedback', label: 'Feedback', icon: MessageCircle },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState('dashboard');

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/superadmin', { replace: true });
      return;
    }

    // Fetch admin profile
    fetch(`${API}/api/admin/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        if (res.status === 401 || res.status === 403) {
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminData');
          navigate('/superadmin', { replace: true });
          return null;
        }
        return res.json();
      })
      .then((data) => {
        if (data?.admin) setAdmin(data.admin);
      })
      .catch(() => setError('Could not load admin data'))
      .finally(() => setLoading(false));

    // Fetch contact form submissions
    fetch(`${API}/api/admin/contact-submissions`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => setSubmissions(data.submissions || []))
      .catch(() => {});
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminData');
    navigate('/superadmin', { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1c2560] text-gray-300">
        Loading...
      </div>
    );
  }

  const activeLabel = NAV_ITEMS.find((n) => n.key === activeSection)?.label || 'Dashboard';

  return (
    <div className="min-h-screen flex bg-[#1c2560]">
      {/* ── Sidebar ── */}
      <aside className="w-64 shrink-0 bg-[#20297a] flex flex-col">
        {/* Brand */}
        <div className="flex items-center gap-3 px-5 py-6 border-b border-white/10">
          <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
            <Shield size={20} className="text-white" strokeWidth={2} />
          </div>
          <div className="min-w-0">
            <div className="text-white font-bold text-[15px] leading-tight truncate">
              InvoiceCloud
            </div>
            <div className="text-indigo-300/70 text-xs leading-tight truncate">
              Super Admin
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV_ITEMS.map(({ key, label, icon: Icon }) => {
            const isActive = activeSection === key;
            return (
              <button
                key={key}
                onClick={() => setActiveSection(key)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition
                  ${isActive
                    ? 'bg-white/15 text-white font-semibold shadow-sm'
                    : 'text-indigo-200/80 hover:bg-white/5 hover:text-white'
                  }`}
              >
                <Icon size={18} strokeWidth={isActive ? 2.25 : 1.75} />
                <span>{label}</span>
              </button>
            );
          })}
        </nav>

        {/* Logout pinned at bottom */}
        <div className="px-3 py-4 border-t border-white/10">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-indigo-200/80 hover:bg-red-500/20 hover:text-white transition"
          >
            <LogOut size={18} strokeWidth={1.75} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* ── Main content ── */}
      <div className="flex-1 min-w-0">
        <header className="flex items-center justify-between px-8 py-5 border-b border-white/10">
          <h1 className="text-xl font-bold text-white">{activeLabel}</h1>
          <button
            onClick={handleLogout}
            className="text-sm bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg transition"
          >
            Logout
          </button>
        </header>

        <main className="p-8">
          {error && (
            <div className="text-sm text-red-400 bg-red-900/30 border border-red-800 rounded-lg px-3 py-2 mb-4">
              {error}
            </div>
          )}

          {activeSection === 'dashboard' && (
            <>
              {admin && (
                <div className="bg-[#242e82] rounded-2xl shadow-xl p-6 max-w-md">
                  <h2 className="text-lg font-semibold mb-4 text-white">Admin Profile</h2>
                  <dl className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-indigo-300/70">Name</dt>
                      <dd className="text-white">{admin.name}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-indigo-300/70">Email</dt>
                      <dd className="text-white">{admin.email}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-indigo-300/70">Role</dt>
                      <dd className="capitalize text-white">{admin.role}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-indigo-300/70">Status</dt>
                      <dd className="text-white">{admin.is_active ? 'Active' : 'Inactive'}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-indigo-300/70">Last login</dt>
                      <dd className="text-white">
                        {admin.last_login ? new Date(admin.last_login).toLocaleString() : '—'}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-indigo-300/70">Created</dt>
                      <dd className="text-white">
                        {admin.created_at ? new Date(admin.created_at).toLocaleDateString() : '—'}
                      </dd>
                    </div>
                  </dl>
                </div>
              )}

              {/* Quick stat cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6 max-w-3xl">
                <div className="bg-[#242e82] rounded-2xl p-5">
                  <div className="text-indigo-300/70 text-xs mb-1">Contact Submissions</div>
                  <div className="text-2xl font-bold text-white">{submissions.length}</div>
                </div>
                <div className="bg-[#242e82] rounded-2xl p-5">
                  <div className="text-indigo-300/70 text-xs mb-1">Admin Role</div>
                  <div className="text-2xl font-bold text-white capitalize">
                    {admin?.role || '—'}
                  </div>
                </div>
                <div className="bg-[#242e82] rounded-2xl p-5">
                  <div className="text-indigo-300/70 text-xs mb-1">Status</div>
                  <div className="text-2xl font-bold text-white">
                    {admin?.is_active ? 'Active' : 'Inactive'}
                  </div>
                </div>
              </div>
            </>
          )}

          {activeSection === 'contact-forms' && (
            <div className="bg-[#242e82] rounded-2xl shadow-xl p-6">
              <h2 className="text-lg font-semibold mb-4 text-white">Contact Form Submissions</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-indigo-300/70 text-left">
                      <th className="pb-2 pr-4">Name</th>
                      <th className="pb-2 pr-4">Email</th>
                      <th className="pb-2 pr-4">Phone</th>
                      <th className="pb-2 pr-4">Plan</th>
                      <th className="pb-2 pr-4">Message</th>
                      <th className="pb-2 pr-4">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.map((s) => (
                      <tr key={s.id} className="border-t border-white/10 text-white">
                        <td className="py-2 pr-4">{s.name}</td>
                        <td className="py-2 pr-4">{s.email}</td>
                        <td className="py-2 pr-4">{s.phone}</td>
                        <td className="py-2 pr-4 capitalize">{s.plan}</td>
                        <td className="py-2 pr-4 max-w-xs truncate">{s.message}</td>
                        <td className="py-2 pr-4">
                          {s.created_at ? new Date(s.created_at).toLocaleString() : '—'}
                        </td>
                      </tr>
                    ))}
                    {submissions.length === 0 && (
                      <tr>
                        <td colSpan="6" className="py-4 text-indigo-300/50 text-center">
                          No submissions yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!['dashboard', 'contact-forms'].includes(activeSection) && (
            <div className="bg-[#242e82] rounded-2xl shadow-xl p-10 text-center max-w-xl">
              <p className="text-white font-semibold mb-1">{activeLabel}</p>
              <p className="text-indigo-300/70 text-sm">
                This section isn't wired up to the backend yet — build the API route and
                fetch it here the same way Dashboard/Contact Forms do.
              </p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}